import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Steps,
  Button,
  Form,
  Input,
  Select,
  Card,
  Row,
  Col,
  Radio,
  Table,
  InputNumber,
  Typography,
  Divider,
  Descriptions,
  Space,
  Alert,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import { toast } from "react-toastify";
import axios from "axios";
import { getSingleRate } from "../../redux/actions/product/addProductAction";
import { loadAllProductSubCategory } from "../../redux/actions/productSubCategory/getProductSubCategory";
import LayerSelectorBasic from "../Card/LayerSelectorBasic";
import LayerSelectorAdvance from "../Card/LayerSelectorAdvance";
import LayerTemplateSelector from "../Card/LayerTemplateSelector";
import PrintSelector from "../Card/PrintSelector";
import CoatingSelector from "../Card/CoatingSelector";
import data from "../../data/rawMaterialData";
import { buildLayersForPly } from "../../utils/layerUtils";

const { Step } = Steps;
const { Title, Text } = Typography;

const layerObj = {
  layerId: 0,
  layerType: "",
  materialType: "",
  gsm: "",
  bf: "",
  ff: "",
};

const QuotationItemModal = ({ visible, onCancel, onSave, initialData, pricingType = "individual" }) => {
  const dispatch = useDispatch();
  const { unitType, ply, castingType } = data;

  const subCategory = useSelector(
    (state) => state.productSubCategories?.list
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Item source toggle
  const [itemSource, setItemSource] = useState("new"); // "new" | "running_size"
  const [runningSizes, setRunningSizes] = useState([]);
  const [loadingRS, setLoadingRS] = useState(false);

  const isCombined = pricingType === "inner_combined" || pricingType === "outer_combined";

  // Rate calculator state
  const [isPrinting, setIsPrinting] = useState(false);
  const [isCoating, setIsCoating] = useState(false);
  const [layers, setLayers] = useState([]);
  const [layerSelector, setLayerSelector] = useState(false);
  const [gsm, setGsm] = useState([]);
  const [bf, setBf] = useState([]);
  const [printing, setPrinting] = useState({});
  const [coating, setCoating] = useState({ coatings: [] });

  // Calculation result & combined item data
  const [calculationResult, setCalculationResult] = useState(null);
  const [formSnapshot, setFormSnapshot] = useState(null);

  // Pricing overrides (step 2)
  const [markupPercent, setMarkupPercent] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [itemName, setItemName] = useState("");
  const [itemDescription, setItemDescription] = useState("");

  // Item Group detection
  const [groupInfo, setGroupInfo] = useState(null);   // group if selected RS is a parent
  const [addAsGroup, setAddAsGroup] = useState(false); // toggle to add entire group

  // Load subcategories
  useEffect(() => {
    if (visible) {
      dispatch(loadAllProductSubCategory({ page: 1, limit: 100 }));
    }
  }, [dispatch, visible]);

  // Fetch running sizes when toggled
  useEffect(() => {
    if (visible && itemSource === "running_size" && runningSizes.length === 0) {
      fetchRunningSizes();
    }
  }, [visible, itemSource]);

  const fetchRunningSizes = async () => {
    setLoadingRS(true);
    try {
      const { data } = await axios.get("running-size?status=true&page=1&count=500");
      setRunningSizes(data?.data || data?.getAllRunningSize || data || []);
    } catch (error) {
      console.error("Failed to load running sizes", error);
    }
    setLoadingRS(false);
  };

  // Auto-fill form from selected running size
  const handleRunningSizeSelect = async (rsId) => {
    const rs = runningSizes.find((r) => r.id === rsId);
    if (!rs) return;

    form.setFieldsValue({
      subcategory: rs.product_sub_category?.name || "",
      length: rs.length,
      width: rs.width,
      height: rs.height,
      unitType: rs.unit_type,
      casting: rs.casting_type,
      ply: rs.ply,
      topConversionRate: rs.top_conversion_rate,
      boxConversionRate: rs.box_conversion_rate,
      print_selector: rs.is_printing ? "yes" : "no",
      isCoating: rs.is_coating ? "yes" : "no",
      joiningMethod: rs.joining_method || undefined,
    });

    setIsPrinting(rs.is_printing || false);
    setIsCoating(rs.is_coating || false);
    if (!itemName) setItemName(rs.name);

    if (rs.layers) {
      try {
        const parsed = typeof rs.layers === "string" ? JSON.parse(rs.layers) : rs.layers;
        setLayers(parsed);
      } catch { setLayers([]); }
    }
    if (rs.printing) {
      try {
        const parsed = typeof rs.printing === "string" ? JSON.parse(rs.printing) : rs.printing;
        setPrinting(parsed);
      } catch { setPrinting({}); }
    }
    if (rs.coating) {
      try {
        const parsed = typeof rs.coating === "string" ? JSON.parse(rs.coating) : rs.coating;
        setCoating(parsed);
      } catch { setCoating({ coatings: [] }); }
    }

    if (rs.ply) handlePlyChange(rs.ply);

    // Check if this RS is the parent of any item group
    setGroupInfo(null);
    setAddAsGroup(false);
    try {
      const { data: grp } = await axios.get(`item-group/by-parent/${rsId}`);
      if (grp) {
        setGroupInfo(grp);
        setAddAsGroup(true);
      }
    } catch {
      // No group for this RS — that's fine
    }
  };

  // Reset / pre-fill when modal opens
  useEffect(() => {
    if (visible) {
      if (initialData) {
        // Pre-fill from existing item
        setItemName(initialData.item_name || "");
        setItemDescription(initialData.item_description || "");
        setMarkupPercent(initialData.markup_percentage || 0);
        setDiscountPercent(initialData.discount_percentage || 0);

        // Parse layers, printing, coating first
        let parsedLayers = [];
        let parsedPrinting = {};
        let parsedCoating = { coatings: [] };

        if (initialData.layers) {
          try {
            parsedLayers =
              typeof initialData.layers === "string"
                ? JSON.parse(initialData.layers)
                : initialData.layers;
          } catch {
            parsedLayers = [];
          }
        }
        // A brand-new item (no matched RunningSize) has no known layer construction, so
        // `initialData.layers` is empty here. Unlike picking Ply by hand (which runs
        // buildLayersForPly via its onChange handler), setFieldsValue below does not fire that
        // handler, so without this the layers array would stay empty while `ply` shows a value.
        // Seed the correct blank layer slots (Top/Flute/Face/... matching the ply count) so the
        // shape is right; values are still left for the operator to fill in.
        if (parsedLayers.length === 0 && initialData.ply) {
          parsedLayers = buildLayersForPly(initialData.ply, [], layerObj).layersArray;
        }
        if (initialData.printing_config) {
          try {
            parsedPrinting =
              typeof initialData.printing_config === "string"
                ? JSON.parse(initialData.printing_config)
                : initialData.printing_config;
          } catch {
            parsedPrinting = {};
          }
        }
        if (initialData.coating_config) {
          try {
            parsedCoating =
              typeof initialData.coating_config === "string"
                ? JSON.parse(initialData.coating_config)
                : initialData.coating_config;
          } catch {
            parsedCoating = { coatings: [] };
          }
        }

        // Set state
        setLayers(parsedLayers);
        setPrinting(parsedPrinting);
        setCoating(parsedCoating);
        setIsPrinting(initialData.is_printing || false);
        setIsCoating(initialData.is_coating || false);

        // Build form values including layer/print/coat fields
        const formValues = {
          subcategory: initialData.subcategory,
          length: initialData.length,
          width: initialData.width,
          height: initialData.height,
          unitType: initialData.unit_type,
          casting: initialData.casting_type,
          quantity: initialData.quantity,
          ply: initialData.ply,
          topConversionRate: initialData.top_conversion_rate,
          boxConversionRate: initialData.box_conversion_rate,
          layerSelector: "basic",
          print_selector: initialData.is_printing ? "yes" : "no",
          isCoating: initialData.is_coating ? "yes" : "no",
          joiningMethod: initialData.joining_method || undefined,
        };

        // Layer form fields (LayerSelectorBasic uses these form names)
        if (parsedLayers.length >= 1) {
          formValues.materialType = parsedLayers[0]?.materialType || undefined;
          formValues.top_gsm = parsedLayers[0]?.gsm || undefined;
          formValues["top-bf"] = parsedLayers[0]?.bf || undefined;
        }
        if (parsedLayers.length >= 2) {
          formValues.flute_materialType = parsedLayers[1]?.materialType || undefined;
          formValues.flute_gsm = parsedLayers[1]?.gsm || undefined;
          formValues.flute_bf = parsedLayers[1]?.bf || undefined;
          formValues.flute_ff = parsedLayers[1]?.ff || undefined;
        }
        if (parsedLayers.length >= 3) {
          formValues.face_materialType = parsedLayers[2]?.materialType || undefined;
          formValues.face_gsm = parsedLayers[2]?.gsm || undefined;
          formValues.face_bf = parsedLayers[2]?.bf || undefined;
        }

        // Printing form fields
        if (initialData.is_printing && parsedPrinting) {
          formValues.printing_type = parsedPrinting.printingType || undefined;
          formValues.printing_colors = parsedPrinting.printingColors || undefined;
          formValues.printing_sides = parsedPrinting.printingSides || undefined;
          formValues.printing_special_colors = parsedPrinting.printingSpecialColors || undefined;
        }

        // Coating form fields
        if (initialData.is_coating && parsedCoating) {
          formValues.coating_type = parsedCoating.coatings || undefined;
        }

        form.setFieldsValue(formValues);

        // If we have a previous calculation, go to review
        if (initialData.calculated_rate) {
          setCalculationResult({
            RS: initialData.roll_size,
            CS: initialData.cutting_size,
            N: initialData.sheets_per_box,
            M: initialData.boxes_per_sheet,
            NET_GSM: initialData.net_gsm,
            TOP_GSM: initialData.top_gsm,
            boxWeight: initialData.box_weight,
            topRate: initialData.material_cost_top,
            restRate: initialData.material_cost_rest,
            printRate: initialData.print_cost,
            coatingRate: initialData.coating_cost_total,
            laminationRate: initialData.coating_cost_lamination,
            leafingRate: initialData.coating_cost_leafing,
            spotUvRate: initialData.coating_cost_spot_uv,
            netFixedCost: initialData.fixed_cost_total,
            fixedCostPerBox: initialData.fixed_cost_per_box,
            finalRate: initialData.calculated_rate,
            selectedMachines: initialData.selected_machines,
            outsourceJobs: initialData.outsource_jobs,
          });
          setFormSnapshot(initialData);
          setCurrentStep(1);
        } else {
          setCurrentStep(0);
        }
      } else {
        // New item — reset everything
        form.resetFields();
        setCurrentStep(0);
        setCalculationResult(null);
        setFormSnapshot(null);
        setLayers([]);
        setGsm([]);
        setBf([]);
        setIsPrinting(false);
        setIsCoating(false);
        setPrinting({});
        setCoating({ coatings: [] });
        setLayerSelector(false);
        setMarkupPercent(0);
        setDiscountPercent(0);
        setItemName("");
        setItemDescription("");
        setItemSource("new");
        setGroupInfo(null);
        setAddAsGroup(false);
      }
    }
  }, [visible, initialData, form]);

  // Ply change handler – preserves existing layer values
  const handlePlyChange = (plyVal) => {
    const { layersArray, gsmArray, bfArray } = buildLayersForPly(plyVal, layers, layerObj);
    setLayers(layersArray);
    setGsm(gsmArray);
    setBf(bfArray);
  };

  // Calculate rate via existing Redux action
  const handleCalculate = async () => {
    try {
      const values = await form.validateFields();
      const subcat = subCategory?.filter(
        (item) => item.name === values.subcategory
      )[0];

      setLoading(true);
      const formData = {
        subcategory: values.subcategory,
        length: values.length,
        width: values.width,
        height: values.height,
        unitType: values.unitType,
        casting: values.casting,
        quantity: values.quantity,
        ply: values.ply,
        layerSelector: layerSelector,
        layers: layers,
        isPrinting: isPrinting,
        printing: printing,
        isCoating: isCoating,
        coating: coating,
        topConversionRate: values.topConversionRate,
        boxConversionRate: values.boxConversionRate,
        numberOfPieces: subcat?.sheet_size_formula || 1,
      };

      const resp = await dispatch(getSingleRate(formData));

      if (resp.message === "success") {
        setCalculationResult(resp.data);
        setFormSnapshot({
          ...formData,
          ...values,
          packing_role: values.packing_role || null,
          qty_per_pack: values.qty_per_pack || null,
          joiningMethod: values.joiningMethod || null,
        });

        // Auto-generate item name if empty
        if (!itemName) {
          setItemName(
            `${values.subcategory} - ${values.length}×${values.width}×${values.height} ${values.unitType}`
          );
        }

        setCurrentStep(1);
      } else {
        toast.error("Rate calculation failed");
      }
    } catch (error) {
      if (error.errorFields) {
        toast.error("Please fill in all required fields");
      } else {
        toast.error(`Calculation error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Build final item data and save
  const handleSave = () => {
    if (!calculationResult || !formSnapshot) {
      toast.error("Please complete the calculation first");
      return;
    }

    const calcRate = calculationResult.finalRate || 0;
    const adjustedRate =
      calcRate *
      (1 + markupPercent / 100) *
      (1 - discountPercent / 100);
    const quantity = Number(formSnapshot.quantity);

    const itemData = {
      // Display info
      item_name:
        itemName ||
        `${formSnapshot.subcategory} - ${formSnapshot.length}×${formSnapshot.width}×${formSnapshot.height}`,
      item_description: itemDescription,

      // Box specification
      subcategory: formSnapshot.subcategory,
      number_of_pieces: formSnapshot.numberOfPieces || 1,
      length: Number(formSnapshot.length),
      width: Number(formSnapshot.width),
      height: Number(formSnapshot.height),
      unit_type: formSnapshot.unitType,
      casting_type: formSnapshot.casting,
      ply: Number(formSnapshot.ply),
      layers: formSnapshot.layers,

      // Printing
      is_printing: formSnapshot.isPrinting,
      printing_config: formSnapshot.printing,

      // Coating
      is_coating: formSnapshot.isCoating,
      coating_config: formSnapshot.coating,

      // Finishing
      joining_method: formSnapshot.joiningMethod || null,

      // Conversion rates
      top_conversion_rate: Number(formSnapshot.topConversionRate),
      box_conversion_rate: Number(formSnapshot.boxConversionRate),

      // Calculation outputs
      roll_size: calculationResult.RS,
      cutting_size: calculationResult.CS,
      sheets_per_box: calculationResult.N || 1,
      boxes_per_sheet: calculationResult.M || 1,

      // GSM
      net_gsm: calculationResult.NET_GSM,
      top_gsm: calculationResult.TOP_GSM,

      // Costs
      material_cost_top: calculationResult.topRate || 0,
      material_cost_rest: calculationResult.restRate || 0,
      print_cost: calculationResult.printRate || 0,
      coating_cost_total: calculationResult.coatingRate || 0,
      coating_cost_lamination: calculationResult.laminationRate || 0,
      coating_cost_leafing: calculationResult.leafingRate || 0,
      coating_cost_spot_uv: calculationResult.spotUvRate || 0,
      fixed_cost_total: calculationResult.netFixedCost || 0,
      fixed_cost_per_box: calculationResult.fixedCostPerBox || 0,

      // Physical
      box_weight: calculationResult.boxWeight || 0,

      // Machines
      selected_machines: calculationResult.selectedMachines || null,
      outsource_jobs: calculationResult.outsourceJobs || null,

      // Pricing
      quantity: quantity,
      calculated_rate: calcRate,
      markup_percentage: markupPercent,
      discount_percentage: discountPercent,
      rate_per_unit: adjustedRate,
      total_amount: quantity * adjustedRate,

      // Packing fields
      packing_role: formSnapshot.packing_role || null,
      qty_per_pack: formSnapshot.packing_role === "inner"
        ? Number(formSnapshot.qty_per_pack || 1)
        : (formSnapshot.packing_role === "outer" ? 1 : null),

      // Group fields
      item_group_id: (groupInfo && addAsGroup) ? groupInfo.id : null,
      qty_per_group: (groupInfo && addAsGroup)
        ? ((groupInfo.members || []).find((m) => m.is_parent)?.qty_per_group || 1)
        : null,
      is_group_parent: !!(groupInfo && addAsGroup),
    };

    // If adding as a group, also build items for non-parent members
    if (groupInfo && addAsGroup) {
      const parentMember = (groupInfo.members || []).find((m) => m.is_parent);
      const parentQtyPerGroup = parentMember?.qty_per_group || 1;

      const memberItems = (groupInfo.members || [])
        .filter((m) => !m.is_parent)
        .map((m) => {
          const rs = m.running_size;
          const rate = rs?.last_sale_price || 0;
          const memberQty = Math.round(quantity * (m.qty_per_group / parentQtyPerGroup));
          return {
            item_name: m.name,
            item_description: `Part of group: ${groupInfo.name}`,
            subcategory: rs?.product_sub_category?.name || "",
            number_of_pieces: 1,
            length: rs?.length || 0,
            width: rs?.width || 0,
            height: rs?.height || 0,
            unit_type: rs?.unit_type || "mm",
            casting_type: rs?.casting_type || "DIE",
            ply: rs?.ply || 3,
            layers: rs?.layers || "[]",
            is_printing: rs?.is_printing || false,
            printing_config: rs?.printing || null,
            is_coating: rs?.is_coating || false,
            coating_config: rs?.coating || null,
            joining_method: rs?.joining_method || null,
            top_conversion_rate: rs?.top_conversion_rate || 0,
            box_conversion_rate: rs?.box_conversion_rate || 0,
            roll_size: rs?.rs || null,
            cutting_size: rs?.cs || null,
            sheets_per_box: rs?.sheets_per_box || 1,
            boxes_per_sheet: rs?.boxes_per_sheet || 1,
            net_gsm: null,
            top_gsm: null,
            material_cost_top: 0,
            material_cost_rest: 0,
            print_cost: 0,
            coating_cost_total: 0,
            coating_cost_lamination: 0,
            coating_cost_leafing: 0,
            coating_cost_spot_uv: 0,
            fixed_cost_total: 0,
            fixed_cost_per_box: 0,
            box_weight: 0,
            selected_machines: null,
            outsource_jobs: null,
            quantity: memberQty,
            calculated_rate: rate,
            markup_percentage: 0,
            discount_percentage: 0,
            rate_per_unit: rate,
            total_amount: rate * memberQty,
            packing_role: null,
            qty_per_pack: null,
            // Group tracking
            item_group_id: groupInfo.id,
            qty_per_group: m.qty_per_group,
            is_group_parent: false,
          };
        });

      onSave([itemData, ...memberItems]);
    } else {
      onSave(itemData);
    }
  };

  // Rate breakdown table data
  const getRateBreakdown = () => {
    if (!calculationResult) return [];

    const topRate = calculationResult.topRate || 0;
    const restRate = calculationResult.restRate || 0;
    const printRate = calculationResult.printRate || 0;
    const fixedCost = calculationResult.fixedCostPerBox || 0;
    const laminationRate = calculationResult.laminationRate || 0;
    const leafingRate = calculationResult.leafingRate || 0;
    const spotUvRate = calculationResult.spotUvRate || 0;

    return [
      { key: 1, parameter: "Top Layer Rate", value: topRate, total: topRate },
      {
        key: 2,
        parameter: "Rest Layers Rate",
        value: restRate,
        total: topRate + restRate,
      },
      {
        key: 3,
        parameter: "Printing Cost",
        value: printRate,
        total: topRate + restRate + printRate,
      },
      {
        key: 4,
        parameter: "Fixed Cost",
        value: fixedCost,
        total: topRate + restRate + printRate + fixedCost,
      },
      {
        key: 5,
        parameter: "Lamination Rate",
        value: laminationRate,
        total:
          topRate + restRate + printRate + fixedCost + laminationRate,
      },
      {
        key: 6,
        parameter: "Leafing Rate",
        value: leafingRate,
        total:
          topRate +
          restRate +
          printRate +
          fixedCost +
          laminationRate +
          leafingRate,
      },
      {
        key: 7,
        parameter: "Spot UV Rate",
        value: spotUvRate,
        total:
          topRate +
          restRate +
          printRate +
          fixedCost +
          laminationRate +
          leafingRate +
          spotUvRate,
      },
      {
        key: 8,
        parameter: "Net Rate",
        value: calculationResult.finalRate || 0,
        total: calculationResult.finalRate || 0,
      },
    ];
  };

  const breakdownColumns = [
    {
      title: "Rate Component",
      dataIndex: "parameter",
      key: "parameter",
      width: "40%",
    },
    {
      title: "Piecewise Rate",
      dataIndex: "value",
      key: "value",
      width: "30%",
      render: (v) => `₹${Number(v).toFixed(2)}`,
    },
    {
      title: "Cumulative Total",
      dataIndex: "total",
      key: "total",
      width: "30%",
      align: "right",
      render: (t) => `₹${Number(t).toFixed(2)}`,
    },
  ];

  return (
    <Modal
      title={initialData ? "Edit Line Item" : "Add Line Item"}
      open={visible}
      onCancel={onCancel}
      width={1100}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title="Configure Box" description="Specify box details & calculate" />
        <Step title="Review & Add" description="Review rate & add to quotation" />
      </Steps>

      {/* ============ STEP 0: RATE CALCULATOR FORM ============ */}
      {currentStep === 0 && (
        <Form
          form={form}
          name="quotation_item"
          labelCol={{ span: 8 }}
          labelWrap
          wrapperCol={{ span: 16 }}
          initialValues={{
            layerSelector: "basic",
            print_selector: "no",
            isCoating: "no",
          }}
          autoComplete="off"
        >
          {/* Item Source Toggle */}
          <Card
            bordered={false}
            style={{ marginBottom: 16, background: "#f0f5ff" }}
          >
            <Row gutter={16} align="middle">
              <Col xs={24} md={8}>
                <Form.Item label="Item Source" style={{ marginBottom: 0 }}>
                  <Radio.Group
                    value={itemSource}
                    onChange={(e) => {
                      setItemSource(e.target.value);
                      if (e.target.value === "new") {
                        form.resetFields();
                      }
                    }}
                  >
                    <Radio value="new">New Item</Radio>
                    <Radio value="running_size">Running Size</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              {itemSource === "running_size" && (
                <Col xs={24} md={16}>
                  <Select
                    showSearch
                    placeholder="Search running size by name, dimensions..."
                    style={{ width: "100%" }}
                    loading={loadingRS}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={handleRunningSizeSelect}
                    size="large"
                  >
                    {runningSizes.map((rs) => (
                      <Select.Option key={rs.id} value={rs.id}>
                        {rs.name} — {rs.length}×{rs.width}×{rs.height} {rs.unit_type} | {rs.ply} Ply | {rs.product_sub_category?.name || ""} | {rs.customer?.name || ""}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              )}
            </Row>
          </Card>

          {/* Group Detection Banner */}
          {groupInfo && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <Space>
                  <strong>{groupInfo.name}</strong>
                  <span>— This item is the parent of an item group.</span>
                  <Switch
                    checked={addAsGroup}
                    onChange={setAddAsGroup}
                    size="small"
                    checkedChildren="Group ON"
                    unCheckedChildren="Group OFF"
                  />
                </Space>
              }
              description={
                addAsGroup ? (
                  <div style={{ marginTop: 8 }}>
                    <span style={{ marginRight: 8 }}>Saving will add all group members:</span>
                    {(groupInfo.members || []).map((m, i) => (
                      <Tooltip key={i} title={`${m.qty_per_group} × ${m.name} (rate from last sale price)`}>
                        <Tag color={m.is_parent ? "blue" : "default"}>
                          {m.qty_per_group}× {m.name}{m.is_parent ? " (parent)" : ""}
                        </Tag>
                      </Tooltip>
                    ))}
                    <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
                      Non-parent members will be added using their last sale price. Rates can be updated individually.
                    </div>
                  </div>
                ) : (
                  <span style={{ fontSize: 12, color: "#888" }}>Toggle ON to auto-add all group members.</span>
                )
              }
            />
          )}

          <Row gutter={16}>
            {/* Left Column: Sizing */}
            <Col xs={24} lg={10}>
              <Card bordered={false}>
                <Title level={5} className="m-2 text-center">
                  Sizing
                </Title>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  name="subcategory"
                  label="Subcategory"
                  rules={[
                    { required: true, message: "Please select sub-category!" },
                  ]}
                >
                  <Select
                    loading={!subCategory}
                    showSearch
                    placeholder="Select Subcategory"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {subCategory &&
                      subCategory.map((subcat) => (
                        <Select.Option key={subcat.id} value={subcat.name}>
                          {subcat.name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  label="Length"
                  name="length"
                  rules={[{ required: true, message: "Please input length!" }]}
                >
                  <Input type="number" />
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  label="Width"
                  name="width"
                  rules={[{ required: true, message: "Please input width!" }]}
                >
                  <Input type="number" />
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  label="Height"
                  name="height"
                  rules={[{ required: true, message: "Please input height!" }]}
                >
                  <Input type="number" />
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  name="unitType"
                  label="Unit"
                  rules={[
                    { required: true, message: "Please select unit type!" },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Unit"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.includes(input)
                    }
                  >
                    {unitType &&
                      unitType.map((unit) => (
                        <Select.Option key={unit} value={unit}>
                          {unit}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  name="casting"
                  label="Casting"
                  rules={[
                    { required: true, message: "Please select casting!" },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Casting"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children.includes(input)
                    }
                  >
                    {castingType &&
                      castingType.map((casting, i) => (
                        <Select.Option key={i + 1} value={casting.value}>
                          {casting.name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  label="Quantity"
                  name="quantity"
                  rules={[
                    { required: true, message: "Please input Quantity!" },
                  ]}
                >
                  <Input type="number" />
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  name="packing_role"
                  label="Packing Role"
                >
                  <Select allowClear placeholder="Optional">
                    <Select.Option value="inner">Inner Box</Select.Option>
                    <Select.Option value="outer">Outer Carton</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.packing_role !== cur.packing_role}
                >
                  {({ getFieldValue }) =>
                    getFieldValue("packing_role") === "inner" ? (
                      <Form.Item
                        style={{ marginBottom: 15 }}
                        label="Qty per Pack"
                        name="qty_per_pack"
                      >
                        <InputNumber min={1} style={{ width: "100%" }} placeholder="Optional" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  label="Top Conversion Rate /Kg"
                  name="topConversionRate"
                  rules={[
                    {
                      required: true,
                      message: "Please input top conversion rate!",
                    },
                  ]}
                >
                  <Input type="number" />
                </Form.Item>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  label="Box Conversion Rate /Kg"
                  name="boxConversionRate"
                  rules={[
                    {
                      required: true,
                      message: "Please input box conversion rate!",
                    },
                  ]}
                >
                  <Input type="number" />
                </Form.Item>
              </Card>
            </Col>

            {/* Right Column: Customization */}
            <Col xs={24} lg={14}>
              <Card bordered={false}>
                <Title level={5} className="m-2 text-center">
                  Customization
                </Title>

                <Form.Item
                  style={{ marginBottom: 15 }}
                  name="ply"
                  label="Ply / Walls"
                  rules={[
                    { required: true, message: "Please select plys!" },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Ply / Walls"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.includes(input)
                    }
                    onChange={handlePlyChange}
                  >
                    {ply &&
                      ply.map((p) => (
                        <Select.Option key={p.value} value={p.value}>
                          {p.name}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>

                <LayerTemplateSelector
                  ply={layers.length}
                  layers={layers}
                  setLayers={setLayers}
                  gsm={gsm}
                  setGsm={setGsm}
                  bf={bf}
                  setBf={setBf}
                />

                <Form.Item
                  label="Layer Selector"
                  name="layerSelector"
                  style={{ marginBottom: 15 }}
                >
                  <Radio.Group
                    defaultValue="basic"
                    onChange={(e) =>
                      setLayerSelector(e.target.value === "advance")
                    }
                  >
                    <Radio value="basic">Basic</Radio>
                    <Radio value="advance">Advance</Radio>
                  </Radio.Group>
                </Form.Item>

                {!layerSelector ? (
                  <LayerSelectorBasic
                    layers={layers}
                    setLayers={setLayers}
                    layerSelector={layerSelector}
                  />
                ) : (
                  <LayerSelectorAdvance
                    layers={layers}
                    gsm={gsm}
                    bf={bf}
                    setLayers={setLayers}
                    setGsm={setGsm}
                    setBf={setBf}
                    layerSelector={layerSelector}
                  />
                )}

                <Form.Item
                  label="Printing"
                  name="print_selector"
                  style={{ marginBottom: 15 }}
                >
                  <Radio.Group
                    defaultValue="no"
                    onChange={(e) =>
                      setIsPrinting(e.target.value === "yes")
                    }
                  >
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Radio.Group>
                </Form.Item>

                {isPrinting && (
                  <PrintSelector
                    printing={printing}
                    setPrinting={setPrinting}
                    isPrinting={isPrinting}
                  />
                )}

                <Form.Item
                  label="Coating"
                  name="isCoating"
                  style={{ marginBottom: 15 }}
                >
                  <Radio.Group
                    defaultValue="no"
                    onChange={(e) =>
                      setIsCoating(e.target.value === "yes")
                    }
                  >
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                  </Radio.Group>
                </Form.Item>

                {isCoating && (
                  <CoatingSelector
                    coating={coating}
                    setCoating={setCoating}
                    isCoating={isCoating}
                  />
                )}

                <Form.Item
                  style={{ marginBottom: 15 }}
                  name="joiningMethod"
                  label="Glueing"
                >
                  <Radio.Group buttonStyle="solid">
                    <Radio.Button value="stitching">Stitching</Radio.Button>
                    <Radio.Button value="hand_pasting">Hand Pasting</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Card>
            </Col>
          </Row>

          {/* Calculate button */}
          <Row justify="end" style={{ marginTop: 16 }}>
            <Space>
              <Button onClick={onCancel}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleCalculate}
                loading={loading}
                shape="round"
              >
                Calculate Rate
              </Button>
            </Space>
          </Row>
        </Form>
      )}

      {/* ============ STEP 1: REVIEW & SAVE ============ */}
      {currentStep === 1 && calculationResult && (
        <div>
          {/* Item name & description */}
          <Card
            bordered={false}
            style={{ marginBottom: 16, background: "#fafafa" }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <label style={{ fontWeight: 500 }}>Item Name</label>
                <Input
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="e.g. 3-Ply Corrugated Box"
                  style={{ marginTop: 4 }}
                />
              </Col>
              <Col xs={24} md={12}>
                <label style={{ fontWeight: 500 }}>Description (optional)</label>
                <Input
                  value={itemDescription}
                  onChange={(e) => setItemDescription(e.target.value)}
                  placeholder="Short description for quotation"
                  style={{ marginTop: 4 }}
                />
              </Col>
            </Row>
          </Card>

          {/* Calculation summary */}
          <Card bordered={false} style={{ marginBottom: 16 }}>
            <Title level={5}>Calculation Results</Title>
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }}>
              <Descriptions.Item label="Sheet Size (RS × CS)">
                {calculationResult.RS}" × {calculationResult.CS}"
              </Descriptions.Item>
              <Descriptions.Item label="Sheets per Box">
                {calculationResult.N}
              </Descriptions.Item>
              <Descriptions.Item label="Boxes per Sheet">
                {calculationResult.M}
              </Descriptions.Item>
              <Descriptions.Item label="Net GSM">
                {calculationResult.NET_GSM}
              </Descriptions.Item>
              <Descriptions.Item label="Top GSM">
                {calculationResult.TOP_GSM}
              </Descriptions.Item>
              <Descriptions.Item label="Box Weight">
                {Number(calculationResult.boxWeight || 0).toFixed(3)} Kg
              </Descriptions.Item>
              <Descriptions.Item label="Fixed Cost Total">
                ₹{Number(calculationResult.netFixedCost || 0).toLocaleString("en-IN")}
              </Descriptions.Item>
              <Descriptions.Item label="Calculated Rate">
                <Text strong style={{ color: "#52c41a", fontSize: 16 }}>
                  ₹{Number(calculationResult.finalRate || 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Rate breakdown table */}
          <Card bordered={false} style={{ marginBottom: 16 }}>
            <Title level={5}>Detailed Rate Breakup</Title>
            <Table
              dataSource={getRateBreakdown()}
              columns={breakdownColumns}
              pagination={false}
              size="small"
              bordered
            />
          </Card>

          {/* Pricing adjustments */}
          <Card bordered={false} style={{ marginBottom: 16 }}>
            <Title level={5}>Pricing Adjustments</Title>
            <Row gutter={16}>
              <Col xs={24} md={6}>
                <label style={{ fontWeight: 500 }}>Calculated Rate</label>
                <div style={{ fontSize: 16, marginTop: 4 }}>
                  ₹{Number(calculationResult.finalRate || 0).toFixed(2)}
                </div>
              </Col>
              <Col xs={24} md={6}>
                <label style={{ fontWeight: 500 }}>Markup %</label>
                <InputNumber
                  min={0}
                  max={100}
                  value={markupPercent}
                  onChange={(v) => setMarkupPercent(v || 0)}
                  style={{ width: "100%", marginTop: 4 }}
                  formatter={(v) => `${v}%`}
                  parser={(v) => v.replace("%", "")}
                />
              </Col>
              <Col xs={24} md={6}>
                <label style={{ fontWeight: 500 }}>Discount %</label>
                <InputNumber
                  min={0}
                  max={100}
                  value={discountPercent}
                  onChange={(v) => setDiscountPercent(v || 0)}
                  style={{ width: "100%", marginTop: 4 }}
                  formatter={(v) => `${v}%`}
                  parser={(v) => v.replace("%", "")}
                />
              </Col>
              <Col xs={24} md={6}>
                <label style={{ fontWeight: 500 }}>Final Rate / Box</label>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: "bold",
                    color: "#1890ff",
                    marginTop: 4,
                  }}
                >
                  ₹
                  {(
                    (calculationResult.finalRate || 0) *
                    (1 + markupPercent / 100) *
                    (1 - discountPercent / 100)
                  ).toFixed(2)}
                </div>
              </Col>
            </Row>
            <Divider />
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Text type="secondary">Quantity:</Text>{" "}
                <Text strong>{formSnapshot?.quantity}</Text>
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">Total Amount:</Text>{" "}
                <Text strong style={{ fontSize: 16 }}>
                  ₹
                  {(
                    Number(formSnapshot?.quantity || 0) *
                    (calculationResult.finalRate || 0) *
                    (1 + markupPercent / 100) *
                    (1 - discountPercent / 100)
                  ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </Text>
              </Col>
            </Row>
          </Card>

          {/* Group Pricing Summary */}
          {groupInfo && addAsGroup && (() => {
            const parentMember = (groupInfo.members || []).find((m) => m.is_parent);
            const parentOrderQty = Number(formSnapshot?.quantity || 1);
            const parentQtyPerGroup = parentMember?.qty_per_group || 1;
            const parentRate = (calculationResult?.finalRate || 0) * (1 + markupPercent / 100) * (1 - discountPercent / 100);
            const groupTotal = (groupInfo.members || []).reduce((sum, m) => {
              const memberRate = m.is_parent ? parentRate : (m.running_size?.last_sale_price || 0);
              return sum + memberRate * m.qty_per_group;
            }, 0);
            return (
              <Card
                bordered={false}
                style={{ marginBottom: 16, background: "#f0f9ff", borderLeft: "4px solid #1677ff" }}
              >
                <Title level={5}>Group Pricing Summary — {groupInfo.name}</Title>
                {(groupInfo.members || []).map((m, i) => {
                  const rate = m.is_parent ? parentRate : (m.running_size?.last_sale_price || 0);
                  const memberQty = Math.round(parentOrderQty * (m.qty_per_group / parentQtyPerGroup));
                  return (
                    <Row key={i} justify="space-between" style={{ padding: "3px 0" }}>
                      <Col>
                        {m.is_parent && <Tag color="blue">parent</Tag>}{" "}
                        {m.qty_per_group}× {m.name}{" "}
                        <Tag color="default">order qty: {memberQty}</Tag>
                        <span style={{ color: "#888", fontSize: 12 }}> @ ₹{rate.toFixed(2)}</span>
                      </Col>
                      <Col><strong>₹{(rate * m.qty_per_group).toFixed(2)}</strong></Col>
                    </Row>
                  );
                })}
                <Divider style={{ margin: "8px 0" }} />
                <Row justify="space-between">
                  <Col><strong>Group total (1 set)</strong></Col>
                  <Col><strong>₹{groupTotal.toFixed(2)}</strong></Col>
                </Row>
                {parentMember && (
                  <Row justify="space-between" style={{ marginTop: 4 }}>
                    <Col>
                      <span style={{ color: "#1677ff" }}>
                        {parentMember.name}-wise price
                        {parentMember.qty_per_group > 1 && ` (÷ ${parentMember.qty_per_group})`}
                      </span>
                    </Col>
                    <Col>
                      <strong style={{ fontSize: 18, color: "#1677ff" }}>
                        ₹{(groupTotal / parentQtyPerGroup).toFixed(2)}
                      </strong>
                    </Col>
                  </Row>
                )}
                <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
                  All member-wise prices: {(groupInfo.members || []).map((m) => {
                    const memberWise = m.qty_per_group ? groupTotal / m.qty_per_group : 0;
                    return `${m.name}: ₹${memberWise.toFixed(2)}`;
                  }).join(" | ")}
                </div>
              </Card>
            );
          })()}

          {/* Actions */}
          <Row justify="space-between">
            <Button onClick={() => setCurrentStep(0)}>
              ← Back to Edit
            </Button>
            <Button type="primary" onClick={handleSave} shape="round">
              {initialData
                ? "Update Item"
                : addAsGroup && groupInfo
                ? `Add Group (${groupInfo.members?.length} items)`
                : "Add to Quotation"}
            </Button>
          </Row>
        </div>
      )}
    </Modal>
  );
};

export default QuotationItemModal;
